import React, { useEffect, useRef, useState } from "react";
import "./CoverScreen.css";

// Image asset URLs — map to existing dossier evidence
const IMG_WHATSAPP =
  "https://customer-assets.emergentagent.com/job_mobile-cinematic-1/artifacts/dop5em9m_file_0000000089d8720bbc5352b2893f2380.png";
const IMG_FORM =
  "https://customer-assets.emergentagent.com/job_mobile-cinematic-1/artifacts/eezgqpg1_file_00000000ffb071f8a521e923557dcb90.png";
const IMG_PRICING =
  "https://customer-assets.emergentagent.com/job_mobile-cinematic-1/artifacts/gznb6zsq_Screenshot_20260505_134929_ixigo.jpg";

export default function CoverScreen({ onUnlock }) {
  // ── Lightbox state ──
  const [lb, setLb] = useState({ open: false, src: "", alt: "" });
  const openLb = (src, alt) => setLb({ open: true, src, alt });
  const closeLb = () => setLb({ open: false, src: "", alt: "" });

  // ── Lock body scroll when lightbox is open ──
  useEffect(() => {
    if (lb.open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lb.open]);

  // ── ESC key handling for lightbox ──
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") closeLb();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // ── Scroll reveal IntersectionObserver ──
  const rootRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("in");
        });
      },
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // ── Social tag clipboard copy ──
  const handleCopyTag = (e, text) => {
    const btn = e.currentTarget;
    if (btn.dataset.copying === "1") return;
    btn.dataset.copying = "1";
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch (_e) {}
        document.body.removeChild(ta);
      }
    } catch (_err) { /* noop */ }
    const original = btn.dataset.copy || btn.textContent;
    btn.dataset.original = original;
    btn.textContent = "✓ Copied";
    btn.style.color = "var(--gold)";
    btn.style.borderColor = "rgba(196,163,90,0.45)";
    window.setTimeout(() => {
      btn.textContent = btn.dataset.original || original;
      btn.style.color = "";
      btn.style.borderColor = "";
      btn.dataset.copying = "0";
    }, 1500);
  };

  return (
    <div ref={rootRef}>
      {/* Lightbox */}
      <div
        className={`lb${lb.open ? " open" : ""}`}
        id="lb"
        onClick={closeLb}
      >
        <span
          className="lb-close"
          id="lb-close"
          onClick={(e) => {
            e.stopPropagation();
            closeLb();
          }}
        >
          ✕ &nbsp;ESC
        </span>
        <img id="lb-img" src={lb.src} alt={lb.alt} />
      </div>

      {/* Nav */}
      <nav>
        <div className="nav-logo">
          <svg width="24" height="24" viewBox="0 0 24 24">
            <circle
              cx="12"
              cy="12"
              r="9"
              fill="none"
              stroke="rgba(196,163,90,0.18)"
              strokeWidth="1.5"
            />
            <circle
              cx="12"
              cy="12"
              r="9"
              fill="none"
              stroke="url(#ng)"
              strokeWidth="1.5"
              strokeDasharray="51.8 5.6"
              strokeLinecap="round"
              transform="rotate(-90 12 12)"
            />
            <defs>
              <linearGradient id="ng" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#C4A35A" />
                <stop offset="100%" stopColor="#A03030" />
              </linearGradient>
            </defs>
          </svg>
          <span className="nav-wordmark">
            Premium Latency
            <sup
              style={{
                color: "var(--gold)",
                fontSize: "8px",
                verticalAlign: "super",
                fontWeight: 400,
              }}
            >
              ™
            </sup>
          </span>
        </div>
        <div className="nav-status">
          <span className="dot"></span>
          UNDER REVIEW
        </div>
      </nav>

      <main>
        {/* ═══════════════════════════════════════════════
            HERO
        ═══════════════════════════════════════════════ */}
        <section className="pad" style={{ paddingTop: "140px" }}>
          <div className="inner">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0 56px",
                flexWrap: "wrap",
              }}
              className="hero-split"
            >
              {/* Left */}
              <div style={{ flex: 1, minWidth: "280px" }}>
                <div className="hero-tag">
                  <span className="dot"></span>
                  Operational Case Study &nbsp;·&nbsp; May 2026
                </div>
                <h1>
                  Accepted<br />
                  World<em>wide.</em>
                </h1>
                <p className="hero-sub">
                  Support subject to<br />regional availability.
                </p>
              </div>

              {/* Right: Spinner */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <div className="ring-wrap">
                  <svg
                    className="ring-svg"
                    width="180"
                    height="180"
                    viewBox="0 0 180 180"
                  >
                    <circle
                      cx="90"
                      cy="90"
                      r="76"
                      fill="none"
                      stroke="rgba(196,163,90,0.07)"
                      strokeWidth="2"
                    />
                    <circle
                      cx="90"
                      cy="90"
                      r="76"
                      fill="none"
                      stroke="url(#rg)"
                      strokeWidth="2"
                      strokeDasharray="440.1 38.2"
                      strokeLinecap="round"
                    />
                    <defs>
                      <linearGradient
                        id="rg"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="#C4A35A" />
                        <stop offset="65%" stopColor="#D4922A" />
                        <stop offset="100%" stopColor="#A03030" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="ring-center">
                    <div className="ring-pct">92%</div>
                    <div className="ring-label">LOADING</div>
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    className="mono"
                    style={{
                      fontSize: "9px",
                      color: "var(--muted)",
                      letterSpacing: ".14em",
                      animation: "blink 2.4s ease-in-out infinite",
                    }}
                  >
                    RETRYING...
                  </div>
                  <div
                    className="mono"
                    style={{
                      fontSize: "8px",
                      color: "var(--muted)",
                      opacity: 0.35,
                      marginTop: "5px",
                    }}
                  >
                    Attempt 47 of 50
                  </div>
                  <div
                    className="mono"
                    style={{
                      fontSize: "8px",
                      color: "var(--muted)",
                      opacity: 0.22,
                      marginTop: "4px",
                    }}
                  >
                    Estimated wait time: Under Review
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="rule"></div>

        {/* ═══════════════════════════════════════════════
            OBSERVED ERROR STATE
        ═══════════════════════════════════════════════ */}
        <section className="pad-sm reveal">
          <div className="inner-sm" style={{ textAlign: "center" }}>
            <div className="label">Observed Error · travel.mastercard.com</div>
            <div
              style={{
                border: "1px solid rgba(160,48,48,0.3)",
                borderRadius: "6px",
                padding: "32px 28px",
                background: "rgba(160,48,48,0.04)",
              }}
            >
              <p
                style={{
                  fontSize: "clamp(1.1rem,3vw,1.6rem)",
                  fontWeight: 600,
                  color: "var(--white)",
                  marginBottom: "16px",
                  lineHeight: 1.4,
                }}
              >
                "Sorry, something went wrong."
              </p>
              <p
                className="mono"
                style={{
                  fontSize: ".8rem",
                  color: "var(--mid)",
                  lineHeight: 1.8,
                }}
              >
                Please call an expert Lifestyle Manager on &nbsp;
                <span style={{ color: "var(--gold)" }}>0008 004 407 027</span>
                &nbsp;to assist.
              </p>
              <div
                className="rule"
                style={{ margin: "20px auto", maxWidth: "200px" }}
              ></div>
              <p
                className="mono"
                style={{ fontSize: "9px", color: "var(--muted)" }}
              >
                This number is documented as unreachable from Indian mobile
                networks. &nbsp;→ See evidence below.
              </p>
            </div>
          </div>
        </section>

        <div className="rule"></div>

        {/* ═══════════════════════════════════════════════
            THREE CONTRADICTIONS
        ═══════════════════════════════════════════════ */}
        <section className="pad reveal">
          <div className="inner">
            <div className="label" style={{ marginBottom: 0 }}>
              Core Contradictions
            </div>

            <div className="c-card">
              <div className="c-number">01</div>
              <div className="c-statement">
                Support required a profile.<br />
                The platform prevented profile creation.
              </div>
              <div className="c-note">
                Observed across ~50 attempts · multiple browsers · multiple
                devices · multiple ISPs
              </div>
            </div>

            <div className="c-card">
              <div className="c-number">02</div>
              <div className="c-statement">
                The form was completed via autofill.<br />
                The timeout conclusion was not revised.
              </div>
              <div className="c-note">
                Official finding: "delay in completion of the form" · Delivered
                at 22:07 of a recorded call, after farewells
              </div>
            </div>

            <div className="c-card">
              <div className="c-number">03</div>
              <div className="c-statement">
                The matter reached executive escalation pathways.<br />
                The review remains ongoing.
              </div>
              <div className="c-note">
                EVP escalation: no response &nbsp;·&nbsp; CEO escalation:
                "review remains ongoing" &nbsp;·&nbsp; Vendor: discussed pricing
              </div>
            </div>
          </div>
        </section>

        <div className="rule"></div>

        {/* ═══════════════════════════════════════════════
            THREE EVIDENCE CARDS
        ═══════════════════════════════════════════════ */}
        <section className="pad-sm reveal">
          <div className="inner-lg">
            <div className="label" style={{ marginBottom: "20px" }}>
              Selected Evidence
            </div>
            <div className="ev-grid">
              {/* Card 1: WhatsApp loop */}
              <div className="ev-card">
                <div className="ev-tag">01 &nbsp;·&nbsp; Concierge Deadlock</div>
                <div className="ev-headline">
                  Cannot assist without a profile. Cannot create a profile
                  without assistance.
                </div>
                <div className="ev-img-slot">
                  <img
                    className="ev-img"
                    loading="lazy"
                    decoding="async"
                    src={IMG_WHATSAPP}
                    alt="WhatsApp concierge exchange showing support deadlock"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    onClick={(e) =>
                      openLb(e.currentTarget.src, e.currentTarget.alt)
                    }
                  />
                </div>
                <div className="ev-quote">
                  "We're unable to locate your Mastercard Travel &amp;
                  Lifestyle Services profile... Please feel free to call our
                  hotline."
                </div>
                <div className="ev-obs">
                  WhatsApp concierge &nbsp;·&nbsp; 11:54 &nbsp;·&nbsp; Hotline
                  status: unreachable
                </div>
              </div>

              {/* Card 2: Autofill */}
              <div className="ev-card">
                <div className="ev-tag">
                  02 &nbsp;·&nbsp; Root Cause Analysis
                </div>
                <div className="ev-headline">
                  Autofill completed the form in under one second. The finding
                  was not revised.
                </div>
                <div className="ev-img-slot">
                  <img
                    className="ev-img"
                    loading="lazy"
                    decoding="async"
                    src={IMG_FORM}
                    alt="Autofilled registration form with error on travel.mastercard.com"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    onClick={(e) =>
                      openLb(e.currentTarget.src, e.currentTarget.alt)
                    }
                  />
                </div>
                <div className="ev-quote">
                  "The error was caused due to a delay in completion of the
                  form. The page actually timed out."
                </div>
                <div className="ev-obs">
                  Technology team finding &nbsp;·&nbsp; Recorded call
                  &nbsp;·&nbsp; 22:07 timestamp
                </div>
              </div>

              {/* Card 3: Pricing */}
              <div className="ev-card">
                <div className="ev-tag">
                  03 &nbsp;·&nbsp; Pricing Observation
                </div>
                <div className="ev-headline">
                  Access delays coincided with significant pricing variation.
                  Observed differential: approximately ₹10,000.
                </div>
                <div className="ev-img-slot">
                  <img
                    className="ev-img"
                    loading="lazy"
                    decoding="async"
                    src={IMG_PRICING}
                    alt="Third-party platform showing ₹20,706 with 19:45 countdown"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    onClick={(e) =>
                      openLb(e.currentTarget.src, e.currentTarget.alt)
                    }
                  />
                </div>
                <div className="ev-quote">
                  "I would suggest you actually get it — on whichever platform
                  you're getting it."
                </div>
                <div className="ev-obs">
                  MTLS Lifestyle Manager, on record &nbsp;·&nbsp; 06:01
                  &nbsp;·&nbsp; Differential: ~₹10,000
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="rule"></div>

        {/* ═══════════════════════════════════════════════
            92% ENDING
        ═══════════════════════════════════════════════ */}
        <section className="ending-wrap reveal">
          <div
            className="mono"
            style={{
              fontSize: "9px",
              letterSpacing: ".2em",
              color: "var(--muted)",
              marginBottom: "24px",
              textTransform: "uppercase",
            }}
          >
            Executive Review Status &nbsp;·&nbsp; Metaphorical Loading Indicator
          </div>
          <div>
            <span className="ending-num">92</span>
            <span className="ending-pct">%</span>
          </div>
          <div className="ending-bar-track">
            <div className="ending-bar-fill"></div>
          </div>
          <div className="ending-quote">"The review remains ongoing."</div>
          <div
            className="mono"
            style={{
              fontSize: "9px",
              color: "var(--muted)",
              opacity: 0.4,
              marginTop: "8px",
            }}
          >
            — Sayo · Global Customer Delivery &amp; Care · Mastercard · 10 May
            2026
          </div>
        </section>

        <div className="rule"></div>

        {/* ═══════════════════════════════════════════════
            FULL AUDIT CTA
        ═══════════════════════════════════════════════ */}
        <section className="cta-section reveal">
          <div className="cta-label">Full Documentation</div>
          <div className="cta-title">
            Eight sections. Every escalation. Every timestamp.
          </div>
          <div className="cta-sub">
            The complete operational case study — including call logs, email
            transcripts, executive escalation records, and the technology
            team's official finding.
          </div>
          <button type="button" className="cta-btn" onClick={onUnlock}>
            Read the Full Audit
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 7h10M8 3l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className="social-row">
            <button
              className="stag"
              data-copy="@MastercardIndia"
              onClick={(e) => handleCopyTag(e, "@MastercardIndia")}
            >
              @MastercardIndia
            </button>
            <button
              className="stag"
              data-copy="@HDFCBank_Cares"
              onClick={(e) => handleCopyTag(e, "@HDFCBank_Cares")}
            >
              @HDFCBank_Cares
            </button>
            <button
              className="stag"
              data-copy="#MastercardFails"
              onClick={(e) => handleCopyTag(e, "#MastercardFails")}
            >
              #MastercardFails
            </button>
            <button
              className="stag"
              data-copy="#MTLSIndia"
              onClick={(e) => handleCopyTag(e, "#MTLSIndia")}
            >
              #MTLSIndia
            </button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer>
        <p>
          Premium Latency™ &nbsp;·&nbsp; Commentary, satire, and consumer
          documentation &nbsp;·&nbsp; All observations presented reflect the
          documented experience of a single cardholder during the referenced
          period &nbsp;·&nbsp; Not affiliated with Mastercard, Ten Lifestyle
          Group, or HDFC Bank &nbsp;·&nbsp; All trademarks remain property of
          their respective owners &nbsp;·&nbsp; Certain records partially
          redacted to remove personal information.
        </p>
        <div
          className="mono"
          style={{
            fontSize: "9px",
            color: "var(--muted)",
            whiteSpace: "nowrap",
          }}
        >
          May 2026 &nbsp;·&nbsp; India
        </div>
        <div style={{ marginTop: "16px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", display: "flex", gap: "16px", justifyContent: "center" }} className="mono text-[var(--gold)]">
          <a href="/privacy" style={{ color: "var(--gold)", textDecoration: "none" }} className="hover:text-[var(--gold-glow)] transition-colors">Privacy Policy</a>
          <span style={{ color: "var(--line)" }}>·</span>
          <a href="/terms" style={{ color: "var(--gold)", textDecoration: "none" }} className="hover:text-[var(--gold-glow)] transition-colors">Terms &amp; Conditions</a>
        </div>
      </footer>
    </div>
  );
}
